import { useContext, useEffect, useRef } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useIdleTimer } from 'react-idle-timer'
import { PresenceContext } from '../contexts/PresenceContext'
import {
  closePresence,
  getLivePresence,
  getMyPresence,
  getPresenceConfig,
  getPresenceRoster,
  getUserDomainUrls,
  getUserDomains,
  getUserPresence,
  getUserRangeEvents,
  postHeartbeat,
  postPresenceViewerGrant,
} from '../api/react/presence/presence'
import getOrCreateClientSessionId from '../utils/functions/clientSessionId'
import type {
  PresenceRosterParams,
  PresenceTotalsParams,
  PresenceUserDomainUrlsParams,
  PresenceUserRangeEventsParams,
  PresenceViewerGrantPayload,
} from '../types/presence.types'

// ── Query Key Factory ────────────────────────────

export const PRESENCE_KEYS = {
  config: () => ['presence', 'config'] as const,
  live: () => ['presence', 'live'] as const,
  me: (params: PresenceTotalsParams) => ['presence', 'me', params] as const,
  user: (userId: string, params: PresenceTotalsParams) => ['presence', 'user', userId, params] as const,
  roster: (params: PresenceRosterParams) => ['presence', 'roster', params] as const,
  userRangeEvents: (userId: string, params: PresenceUserRangeEventsParams) =>
    ['presence', 'userRangeEvents', userId, params] as const,
  userDomainUrls: (userId: string, params: PresenceUserDomainUrlsParams) =>
    ['presence', 'userDomainUrls', userId, params] as const,
  // Keyed on (userId, day) only — `page` is managed by the infinite query, never
  // part of the cache key (else every page would be its own cache entry).
  userDomains: (userId: string, day: string) =>
    ['presence', 'userDomains', userId, day, 'infinite'] as const,
}

// ── Heartbeat interval (ms) ──────────────────────

const HEARTBEAT_INTERVAL = 60_000
// Leader election (react-idle-timer crossTab) resolves asynchronously a few
// hundred ms after mount — isLeader() is false at the synchronous mount tick.
// We retry the leading-edge heartbeat on this cadence until leadership resolves,
// up to LEADER_WAIT_MAX, after which the steady-state interval takes over.
const LEADER_POLL_INTERVAL = 500
const LEADER_WAIT_MAX = 5_000

// ── Query Hooks ──────────────────────────────────

/**
 * Reads the presence kill-switch flag. `enabled` MUST be the app's
 * auth-ready signal — firing before the Auth0 token getter is wired into the
 * axios interceptor sends the request with no Authorization header and 403s.
 */
export const usePresenceConfig = (enabled = true) =>
  useQuery({
    queryKey: PRESENCE_KEYS.config(),
    queryFn: getPresenceConfig,
    enabled,
    staleTime: 5 * 60_000, // config changes rarely
    refetchOnWindowFocus: false,
  })

/** Phase 4 — live presence list for the presence indicator */
export const useLivePresence = (enabled = true) =>
  useQuery({
    queryKey: PRESENCE_KEYS.live(),
    queryFn: getLivePresence,
    enabled,
    refetchOnWindowFocus: false,
  })

/**
 * My own presence totals for the "My time" summary card. Hits /me/ (the backend
 * resolves the user from the token — no id needed). `refetchInterval` (ms) keeps
 * the live "today so far" value fresh; Redis + bounded Postgres, no heavy load.
 */
export const useMyPresence = (
  params: PresenceTotalsParams,
  enabled = true,
  refetchInterval: number | false = false,
) =>
  useQuery({
    queryKey: PRESENCE_KEYS.me(params),
    queryFn: () => getMyPresence(params),
    enabled,
    refetchInterval,
    refetchOnWindowFocus: false,
  })

/**
 * Phase 5 — another user's presence totals (admin / per-user views).
 * Developer-only on the backend. `enabled` gates the fetch (e.g. only when the
 * drawer is open for a real user id). `refetchInterval` (ms) lets the card poll
 * so the live "today so far" value stays fresh; the request is Redis+bounded
 * Postgres, no heavy DB load.
 */
export const useUserPresence = (
  userId: string,
  params: PresenceTotalsParams,
  enabled = true,
  refetchInterval: number | false = false,
) =>
  useQuery({
    queryKey: PRESENCE_KEYS.user(userId, params),
    queryFn: () => getUserPresence(userId, params),
    enabled: enabled && !!userId,
    refetchInterval,
    refetchOnWindowFocus: false,
  })

/**
 * Phase 5 — team-timelines roster for one Amsterdam day (admin page).
 * Backend cost is bounded (one page of users + one indexed segment query +
 * one page-scoped live read). `refetchInterval` (ms) keeps the live "online
 * now" dots fresh; `placeholderData: (prev) => prev` avoids a full skeleton
 * flash when only the day/page/filter changes.
 */
export const usePresenceRoster = (
  params: PresenceRosterParams,
  enabled = true,
  refetchInterval: number | false = false,
) =>
  useQuery({
    queryKey: PRESENCE_KEYS.roster(params),
    queryFn: () => getPresenceRoster(params),
    enabled,
    refetchInterval,
    placeholderData: (previous) => previous,
    refetchOnWindowFocus: false,
  })

/**
 * Lazy activity log (actions · notices · clock-ins) for ONE user across a Range
 * window — powers the Range expand panel's log column (§14.5). `enabled` gates the
 * fetch to "row is expanded" so it fires ONCE per expand, not for the whole roster.
 * No poll: range data is historical (only today's bar is live, and the log re-fetches
 * on a fresh expand). Cached by (userId, range) so re-expanding the same row is free.
 */
export const useUserRangeEvents = (
  userId: string,
  params: PresenceUserRangeEventsParams,
  enabled = true,
) =>
  useQuery({
    queryKey: PRESENCE_KEYS.userRangeEvents(userId, params),
    queryFn: () => getUserRangeEvents(userId, params),
    enabled: enabled && !!userId && !!params.start_date && !!params.end_date,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000, // historical window — safe to reuse across re-expands
  })

/**
 * Lazy per-URL drill-down (7g L4) for ONE user · ONE domain · ONE day — powers the
 * expandable page list under a 'Top sites' domain row. `enabled` gates the fetch to
 * "domain row is expanded" so it fires ONCE per expand, not for the whole block.
 * Cached by (userId, day, domain) so re-expanding the same domain is free.
 */
export const useUserDomainUrls = (
  userId: string,
  params: PresenceUserDomainUrlsParams,
  enabled = true,
) =>
  useQuery({
    queryKey: PRESENCE_KEYS.userDomainUrls(userId, params),
    queryFn: () => getUserDomainUrls(userId, params),
    enabled: enabled && !!userId && !!params.day && !!params.domain,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000, // day-scoped historical data — reuse across re-expands
  })

/**
 * Lazy, PAGINATED full per-domain list (7f) for ONE user · ONE day — powers the
 * "Other sites" expansion in the 'Top sites' block (infinite scroll). The roster
 * already ships the top-15 inline; this fetches EVERY domain, a page at a time.
 * `enabled` gates it to "Other sites is expanded" so it fires only on demand.
 * Keyed on (userId, day) so re-opening the same day reuses the loaded pages.
 */
export const useUserDomainsInfinite = (
  userId: string,
  day: string,
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: PRESENCE_KEYS.userDomains(userId, day),
    queryFn: ({ pageParam = 1 }) =>
      getUserDomains(userId, { day, page: pageParam as number }),
    initialPageParam: 1,
    // NonCountingInfiniteScrollPagination: hasNext stays true while a full page
    // returns, false on the first short/empty page. Prefer the backend's nextPage;
    // if it ever comes back null while hasNext is true, derive it from the last
    // page param (+1) so the scroll doesn't silently terminate early.
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage?.hasNext ? lastPage.nextPage ?? (lastPageParam as number) + 1 : undefined,
    enabled: enabled && !!userId && !!day,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000, // day-scoped historical data — reuse across re-expands
  })

// ── Grant Mutation ───────────────────────────────

/**
 * Grant or revoke presence-viewing permission for a target user.
 * Caller must hold `canGrantPresence`. Invalidates the user detail and
 * full user list on success so the toggle reflects the new state.
 */
export const useGrantPresenceView = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: PresenceViewerGrantPayload) => postPresenceViewerGrant(payload),
    onSuccess: (_data, variables) => {
      // Refresh the specific user detail so `can_view_presence` is current
      qc.invalidateQueries({ queryKey: ['userInfo', variables.user_id] })
      // Refresh the users list (key prefix used by USER_KEYS.list / .listInfinite)
      qc.invalidateQueries({ queryKey: ['userList'] })
      qc.invalidateQueries({ queryKey: ['userListInfinite'] })
    },
  })
}

// ── Runtime Hook ─────────────────────────────────

/**
 * Core presence runtime — no return value, no rendered output.
 * Mounts an idle timer + a 60-second heartbeat interval when enabled.
 * Fires a keepalive close request on page unload.
 *
 * Must be called from PresenceProvider which gates it behind
 * `presence_heartbeat_enabled` and `!Capacitor.isNativePlatform()`.
 */
export const usePresenceRuntime = (enabled: boolean): void => {
  // Capture the per-tab id once — getOrCreateClientSessionId touches
  // sessionStorage, which must not run on every render (render must stay pure).
  const clientSessionIdRef = useRef<string>(getOrCreateClientSessionId())
  const clientSessionId = clientSessionIdRef.current
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const leadingEdgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // react-idle-timer v5: useIdleTimer returns isLeader() and isIdle() as functions.
  // crossTab + leaderElection ensure only one tab fires heartbeats per user.
  // timeout is the inactivity threshold — matches the backend IDLE_THRESHOLD (5 min).
  const { isLeader, isIdle } = useIdleTimer({
    timeout: 5 * 60_000,
    crossTab: true,
    leaderElection: true,
    // onIdle / onActive are not used here; we poll isIdle() inside the interval instead
    // so the interval logic is the single source of truth for whether to fire.
    startManually: false,
  })

  useEffect(() => {
    if (!enabled) return

    // Baseline for tab_open_ms — local to this effect run so it resets if the
    // runtime is disabled then re-enabled (a ref initialized at mount would
    // keep counting across the disabled gap and inflate the active-time value).
    const enabledAt = Date.now()

    const fireHeartbeat = (): boolean => {
      // Only the elected leader fires heartbeats. We now ping while the tab is
      // open regardless of idle/hidden — the heartbeat firing at all is the
      // proof the tab is open (the Activity-% denominator). is_active tells the
      // backend whether this ping builds the Active timeline (foreground +
      // interacting) or only keeps the Tab-Open span alive (idle/backgrounded).
      // Returns false when nothing was sent (used by the leading-edge retry to
      // decide whether to keep waiting for leadership to resolve).
      if (!isLeader()) return false

      const tab_open_ms = Date.now() - enabledAt
      const is_active = !isIdle() && !document.hidden
      postHeartbeat({ client_session_id: clientSessionId, tab_open_ms, is_active }).catch(() => {
        // Heartbeat failures are non-fatal — the backend handles gaps gracefully
      })
      return true
    }

    // Leading edge: open the segment / mark the user live ASAP instead of waiting
    // a full HEARTBEAT_INTERVAL. Leader election is async, so isLeader() is false
    // at mount — retry on a short cadence until leadership resolves (or give up
    // after LEADER_WAIT_MAX and let the steady-state interval take over).
    const startedAt = Date.now()
    const tryLeadingEdge = () => {
      if (fireHeartbeat()) return // sent (or leader-but-paused) — done
      if (Date.now() - startedAt >= LEADER_WAIT_MAX) return // gave up; interval covers it
      leadingEdgeTimeoutRef.current = setTimeout(tryLeadingEdge, LEADER_POLL_INTERVAL)
    }
    tryLeadingEdge()

    intervalRef.current = setInterval(fireHeartbeat, HEARTBEAT_INTERVAL)

    // ── Visibility change ────────────────────────
    // Fire on EVERY transition (both directions) so the backend learns the
    // open/active state change immediately instead of waiting up to 60s — and,
    // when backgrounding, before browser throttling slows the interval. Going
    // hidden sends is_active=false (keeps the Tab-Open span alive); becoming
    // visible sends the real foreground/active state.
    const handleVisibilityChange = () => {
      if (!isLeader()) return
      const tab_open_ms = Date.now() - enabledAt
      const is_active = !isIdle() && !document.hidden
      postHeartbeat({ client_session_id: clientSessionId, tab_open_ms, is_active }).catch(() => {})
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // ── Unload close (keepalive fetch) ───────────
    // pagehide is preferred over beforeunload — it fires on bfcache eviction too.
    // Both can fire on a single tab close, so a one-shot guard prevents a
    // duplicate close request. Firing this tab's csid is harmless to other tabs
    // (csid is per-tab); backend stale-sweep salvages a missed close anyway.
    let closed = false
    const fireClose = () => {
      if (closed) return
      closed = true
      closePresence({ client_session_id: clientSessionId, reason: 'unload' })
    }
    window.addEventListener('pagehide', fireClose)
    window.addEventListener('beforeunload', fireClose)

    return () => {
      if (leadingEdgeTimeoutRef.current !== null) {
        clearTimeout(leadingEdgeTimeoutRef.current)
        leadingEdgeTimeoutRef.current = null
      }
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', fireClose)
      window.removeEventListener('beforeunload', fireClose)
    }
    // isLeader / isIdle are stable refs from react-idle-timer (read at call-time
    // inside the handlers), so they are intentionally NOT in the deps — listing
    // them would risk tearing down the interval on every leader-election change.
  }, [enabled, clientSessionId])
}

// ── Consumer Hook ────────────────────────────────

export const usePresence = () => {
  const ctx = useContext(PresenceContext)
  if (!ctx) throw new Error('usePresence must be used within PresenceProvider')
  return ctx
}
